import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { first, map, switchMap } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { SecondMs } from 'rev-shared/date/Time.Constant';
import { audioQueryConstraints, videoQueryConstraints } from 'rev-capture/Capture.Constants';
import { getPlaceholderVideo, stopStream, getScaledVideoStream } from 'rev-shared/webrtc/streamUtil';
import { isMobile, isSafari } from 'rev-shared/util/UserAgentUtil';

import { WebcastModel } from 'rev-portal/scheduledEvents/webcast/model/WebcastModel';
import { ListenerProducerService } from 'rev-shared/webrtc/ListenerProducer.Service';
import { SelfProducedService } from './SelfProduced.Service';

import styles from './PresenterView.module.less';

@Component({
	host: {
		'[class]': 'styles.root'
	},
	selector: 'presenter-view',
	templateUrl: './PresenterView.Component.html',
	standalone: false
})
export class PresenterViewComponent implements OnInit, OnDestroy {
	@Input() public webcast: WebcastModel;

	public readonly styles = styles;
	public readonly isMobile = isMobile();
	public readonly isSafari = isSafari();

	public cameraEnabled: boolean;
	public micEnabled: boolean = true;
	public selectedCamera: MediaDeviceInfo;
	public lastSelectedCamera: MediaDeviceInfo;
	public selectedMic: MediaDeviceInfo;
	public selectedCameraLabel: string;
	public selectedMicLabel: string;
	public cameraDevices: MediaDeviceInfo[];
	public micDevices: MediaDeviceInfo[];
	public videoStarted: boolean;
	public error: boolean;

	public cameraStream: MediaStream;
	public micStream: MediaStream;
	public displayCaptureStream: MediaStream;
	public placeholderStream: MediaStream;
	public speakerStream: MediaStream;

	public cancelPlaceholderStream: () => void;
	public cancelCaptureStream: () => void;

	public showBroadcastCountdown: boolean;
	public countdownSecs: number;
	public countdownTimer: number;

	private sub: Subscription;

	constructor(
		public readonly SelfProducedService: SelfProducedService,
		public readonly ListenerProducerService: ListenerProducerService
	) {}

	public ngOnInit(): void {
		console.log('init presenter view');

		this.sub = this.SelfProducedService.onBroadcastStarting(this.webcast.id).subscribe(
			() => this.startBroadcastCountdown());

		this.sub.add(
			this.webcast.webcast$.pipe(
				map(w => !w.vcStatus.isInitializing && w.webRtcListenerUrl),
				first<string>(Boolean),
				switchMap(listenerUrl => {
					console.log('listenerUrl: ', listenerUrl);
					return this.ListenerProducerService.init(this.webcast.id, listenerUrl, true);
				}),
				switchMap(() => {
					console.log('Presenter View. Listener connection started');

					this.ListenerProducerService.setMicStream(this.micStream, this.micEnabled);

					return Promise.all([
						this.displayCaptureStream && this.ListenerProducerService.setDisplayCaptureStream(this.displayCaptureStream),
						this.cameraStream && this.ListenerProducerService.setCameraStream(this.cameraStream),
						this.placeholderStream && this.ListenerProducerService.setPlaceholderVideo(this.placeholderStream),
						(this.displayCaptureStream || this.cameraStream || this.micStream) &&
							this.ListenerProducerService.updateAudioProducer()
					]).then(() => {
						console.log('Presenter View. Producers initialized');
						return this.ListenerProducerService.tryStartRecordingSelfProduced();
					});
				})
			)
				.subscribe({
					error: e => {
						console.error('Error starting listener connection: ', e);
						this.error = true;
					}
				}));

		this.loadCaptureDevices();
	}

	public ngOnDestroy(): void {
		this.sub?.unsubscribe();
		this.ListenerProducerService.stop(!this.webcast.currentRun.isStarted);
		this.stopCamera();
		this.stopMic();
		stopStream(this.displayCaptureStream);
		this.cancelCaptureStream?.();
	}

	public loadCaptureDevices(): Promise<void> {
		this.cameraDevices = null;
		this.micDevices = null;

		return navigator.mediaDevices.enumerateDevices()
			.then(devices => {
				this.cameraDevices = devices.filter(d => d.deviceId && d.kind === 'videoinput');
				this.micDevices = devices.filter(d => d.deviceId && d.kind === 'audioinput');
			})
			.catch(e => console.error('Error Loading Devics: ', e));
	}

	public setCamera(enabled: boolean, device: MediaDeviceInfo): void {
		this.selectedCamera = device;
		this.cameraEnabled = enabled;
		this.videoStarted = true;

		if(enabled) {
			this.lastSelectedCamera = device;
		}

		Promise.all([
			this.initCameraStream(),
			!this.micStream && this.micEnabled && this.initMic()
		])
			.then(() => this.ListenerProducerService.tryStartRecordingSelfProduced())
			.catch(e => console.error('Error starting camera stream: ', e));
	}

	public setMic(enabled: boolean, device: MediaDeviceInfo): void {
		const toggle = this.micStream && this.selectedMic === device;
		this.selectedMic = device;
		this.micEnabled = enabled;

		if (toggle) {
			this.ListenerProducerService.toggleMic(this.micEnabled);
		}
		else {
			this.initMic()
				.catch(e => console.error('error initMic: ', e));
		}
	}

	private initMic(updateAudioProducer: boolean = true): Promise<void> {
		if(!this.micEnabled) {
			this.stopMic();
			this.ListenerProducerService.setMicStream(null);
			return this.ListenerProducerService.updateAudioProducer();
		}
		else if (this.micStream) {
			stopStream(this.micStream);
		}

		return this.getUserMedia({
			audio: this.getConstraint(this.micEnabled, this.selectedMic, audioQueryConstraints),
		})
			.then(stream => {
				this.micStream = stream;
				this.selectedMicLabel = stream.getAudioTracks()[0]?.label;
				stream.getAudioTracks().forEach(t => t.addEventListener('ended', () => {
					console.log('audio track ended');
					this.stopMic();
				}));

				this.ListenerProducerService.setMicStream(this.micStream);
				if (updateAudioProducer) {
					return this.ListenerProducerService.updateAudioProducer();
				}
				return Promise.resolve();
			})
			.catch(e => {
				console.error('getUserMedia error, audio: ', e);
				this.stopMic();
				this.ListenerProducerService.setMicStream(null);
				if (updateAudioProducer) {
					return this.ListenerProducerService.updateAudioProducer();
				}
				return Promise.resolve();
			});
	}

	public toggleMic(): void {
		this.setMic(
			!this.micEnabled || !this.micStream,
			this.selectedMic
		);
	}

	private getUserMedia(constraint: MediaStreamConstraints): Promise<MediaStream> {
		console.log('calling getUserMedia', constraint);
		return navigator.mediaDevices.getUserMedia(constraint)
			.then(stream => this.loadCaptureDevices().then(() => stream));
	}

	private stopCamera(): void {
		stopStream(this.cameraStream);
		this.speakerStream = null;
		this.cameraEnabled = false;
		this.cameraStream = null;
		this.selectedCamera = null;
	}

	public initCameraStream(): Promise<void> {
		this.speakerStream = null;
		if(!this.cameraEnabled) {
			this.stopCamera();
			return this.tryInitPlaceholderStream();
		}

		this.stopPlaceholderStream();

		return this.getUserMedia({
			video: this.getConstraint(this.cameraEnabled, this.selectedCamera, videoQueryConstraints)
		})
			.then(stream => {
				console.log('camera loaded');
				stopStream(this.cameraStream);
				this.cameraStream = stream;
				this.speakerStream = stream;
				this.selectedCameraLabel = stream.getVideoTracks()[0]?.label;

				stream.getVideoTracks().forEach(t => t.addEventListener('ended', () => {
					console.log('video track ended');
					this.stopCamera();
					this.tryInitPlaceholderStream()
						.catch(e => console.error('Error in init placeholder stream: ', e));
				}));

				return this.ListenerProducerService.setCameraStream(stream);
			})
			.catch(e => {
				console.error('getUserMedia error: ', e);
				this.stopCamera();
				return Promise.reject(e);
			});
	}

	public stopCapture(): Promise<void> {
		this.cancelCaptureStream?.();
		if(this.displayCaptureStream) {
			stopStream(this.displayCaptureStream);
			this.displayCaptureStream = null;
			return this.ListenerProducerService.stopContent();
		}

		return Promise.resolve();
	}

	public stopMic(): void {
		stopStream(this.micStream);
		this.micStream = null;
		this.selectedMic = null;
		this.micEnabled = false;
	}

	public startCapture(): void {
		(navigator.mediaDevices as any).getDisplayMedia({
			video: videoQueryConstraints,
			audio: audioQueryConstraints
		})
			.then(stream => {
				Promise.all([
					!this.videoStarted && this.initCameraStream(),
					!this.micStream && this.micEnabled && this.initMic(false)
				])
					.then(() => {
						this.stopCapture()
							.then(() => {
								if (!this.isSafari) {
									stream.getVideoTracks().forEach(t => t.addEventListener('ended', () => this.stopCapture()));
									const [captureStream, close] = getScaledVideoStream(stream);
									this.displayCaptureStream = captureStream;
									this.cancelCaptureStream = close;
								}
								else {
									this.displayCaptureStream = stream;
								}

								return this.ListenerProducerService.setDisplayCaptureStream(this.displayCaptureStream);
							})
							.then(() => this.ListenerProducerService.updateAudioProducer())
							.then(() => this.ListenerProducerService.tryStartRecordingSelfProduced());
					});
			})
			.catch(e => {
				console.error('getDisplayMedia error: ', e);
			});
	}

	private getConstraint(enabled: boolean, device: MediaDeviceInfo, constraints?: any): any {
		if(!enabled) {
			return false;
		}
		return device ?
			{ deviceId: { exact: device.deviceId }, ...constraints } :
			(constraints || true);
	}

	private tryInitPlaceholderStream(): Promise<void> {
		if(this.placeholderStream) {
			return Promise.resolve();
		}

		const [stream, close] = getPlaceholderVideo();
		this.placeholderStream = stream;
		this.cancelPlaceholderStream = close;
		this.speakerStream = this.placeholderStream;
		return this.ListenerProducerService.setPlaceholderVideo(this.placeholderStream);
	}

	private stopPlaceholderStream(): void {
		this.cancelPlaceholderStream?.();
		this.cancelPlaceholderStream = null;
		this.placeholderStream = null;
	}

	private startBroadcastCountdown(): void {
		this.showBroadcastCountdown = true;
		this.countdownSecs = 4;

		this.countdownTimer = window.setInterval(() => {
			this.countdownSecs = this.countdownSecs - 1;
			if(!(this.countdownSecs >= 0)) {
				this.stopBroadcastCountdown();
			}
		}, 1 * SecondMs);
	}

	private stopBroadcastCountdown(): void {
		window.clearInterval(this.countdownTimer);
		this.countdownTimer = null;
		this.countdownSecs = null;
		this.showBroadcastCountdown = false;
	}
}
